import {
  ActionIcon,
  Alert,
  Button,
  Card,
  FileButton,
  Group,
  Image,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPencil, IconTrash, IconUpload } from "@tabler/icons-react";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import { EmptyState, LoadingState, PageHeader } from "@how/ui";
import {
  ACCEPTED_IMAGE_TYPES,
  mediaUrl,
  useDeleteAsset,
  useUpdateAsset,
  useUploadAsset,
} from "./media-queries";
import { useAssets, type AssetRow } from "./website-queries";
import { WebsiteTabs } from "./WebsiteTabs";

const DESCRIPTION =
  "Images your church can put on a page. Every image is described before it is uploaded.";

/** What an image is of, in one line, for a reader who cannot see it. Required, and it is not decoration. */
const ALT_HELP =
  "Describe what the image shows, as you would to someone on the phone. A page can mark it decorative later if it carries no meaning.";

function UploadModal({
  opened,
  onClose,
}: {
  readonly opened: boolean;
  readonly onClose: () => void;
}) {
  const upload = useUploadAsset();
  const [file, setFile] = useState<File | null>(null);
  const [altText, setAltText] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const close = () => {
    setFile(null);
    setAltText("");
    setProblem(null);
    onClose();
  };

  return (
    <Modal opened={opened} onClose={close} title="Add an image" radius="lg">
      <Stack gap="sm">
        <Group>
          <FileButton
            onChange={(chosen) => {
              setFile(chosen);
              setProblem(null);
            }}
            accept={ACCEPTED_IMAGE_TYPES.join(",")}
          >
            {(props) => (
              <Button {...props} variant="default" leftSection={<IconUpload size={16} />}>
                Choose a file
              </Button>
            )}
          </FileButton>
          <Text size="sm" c={file === null ? "dimmed" : "var(--how-text-primary)"}>
            {file?.name ?? "JPEG, PNG, WebP or AVIF, up to 10 MB"}
          </Text>
        </Group>
        {file === null ? null : (
          <Image
            src={URL.createObjectURL(file)}
            alt=""
            mah={220}
            fit="contain"
            radius="md"
            bg="var(--how-surface-sunken)"
          />
        )}
        <TextInput
          label="What does it show?"
          description={ALT_HELP}
          required
          value={altText}
          onChange={(event) => {
            setAltText(event.currentTarget.value);
          }}
        />
        {problem === null ? null : (
          <Text size="sm" c="red">
            {problem}
          </Text>
        )}
        <Group justify="flex-end" mt="xs">
          <Button variant="subtle" onClick={close}>
            Cancel
          </Button>
          <Button
            loading={upload.isPending}
            disabled={file === null || altText.trim().length === 0}
            onClick={() => {
              if (file === null) return;
              upload.mutate(
                { file, altText },
                {
                  onSuccess: () => {
                    notifications.show({
                      color: "teal",
                      title: "Image added",
                      message: altText.trim(),
                    });
                    close();
                  },
                  onError: (error: Error) => {
                    setProblem(
                      error.message.length > 0
                        ? error.message
                        : "The image was not added. Check your access and try again.",
                    );
                  },
                },
              );
            }}
          >
            Add image
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function AssetCard({ asset }: { readonly asset: AssetRow }) {
  const updateAsset = useUpdateAsset();
  const deleteAsset = useDeleteAsset();
  const canManage = usePermission("media.manage", { write: true });
  const [editing, setEditing] = useState(false);
  const [altText, setAltText] = useState(asset.alt_text);
  const [confirming, setConfirming] = useState(false);
  const editable = canManage.data === true;

  return (
    <Card padding="sm">
      <Stack gap="xs">
        <Image
          src={mediaUrl(asset.storage_path)}
          alt={asset.alt_text}
          h={160}
          fit="contain"
          radius="md"
          bg="var(--how-surface-sunken)"
        />
        <Text size="sm" fw={600} lineClamp={2}>
          {asset.alt_text}
        </Text>
        <Group justify="space-between" align="center">
          <Text size="xs" c="dimmed">
            {asset.width === null || asset.height === null
              ? asset.mime_type.replace("image/", "").toUpperCase()
              : `${String(asset.width)} × ${String(asset.height)}`}
          </Text>
          {editable ? (
            <Group gap={4}>
              <Tooltip label="Change the description">
                <ActionIcon
                  variant="subtle"
                  aria-label={`Change the description of ${asset.alt_text}`}
                  onClick={() => {
                    setAltText(asset.alt_text);
                    setEditing(true);
                  }}
                >
                  <IconPencil size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Remove">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={`Remove ${asset.alt_text}`}
                  onClick={() => {
                    setConfirming(true);
                  }}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>
          ) : null}
        </Group>
      </Stack>

      <Modal
        opened={editing}
        onClose={() => {
          setEditing(false);
        }}
        title="Change the description"
        radius="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="What does it show?"
            description={ALT_HELP}
            required
            value={altText}
            onChange={(event) => {
              setAltText(event.currentTarget.value);
            }}
          />
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => {
                setEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              loading={updateAsset.isPending}
              disabled={altText.trim().length === 0}
              onClick={() => {
                updateAsset.mutate(
                  { assetId: asset.id, altText },
                  {
                    onSuccess: () => {
                      setEditing(false);
                    },
                    onError: () => {
                      notifications.show({
                        color: "red",
                        title: "Not changed",
                        message: "Check your access and try again.",
                      });
                    },
                  },
                );
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title="Remove this image?"
        radius="lg"
      >
        <Stack gap="sm">
          <Text size="sm">
            It disappears from this library and from any draft using it. A page that was already
            published keeps showing it until that page is published again, because a published page
            keeps the address it was published with.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => {
                setConfirming(false);
              }}
            >
              Keep it
            </Button>
            <Button
              color="red"
              loading={deleteAsset.isPending}
              onClick={() => {
                deleteAsset.mutate(
                  { assetId: asset.id, storagePath: asset.storage_path },
                  {
                    onSuccess: () => {
                      setConfirming(false);
                      notifications.show({
                        color: "teal",
                        title: "Image removed",
                        message: asset.alt_text,
                      });
                    },
                    onError: () => {
                      notifications.show({
                        color: "red",
                        title: "Not removed",
                        message: "Check your access and try again.",
                      });
                    },
                  },
                );
              }}
            >
              Remove
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}

/** The church's image library: what the image fields on a page choose from. */
export function MediaPage() {
  const assets = useAssets();
  const canManage = usePermission("media.manage", { write: true });
  const [adding, setAdding] = useState(false);

  return (
    <>
      <PageHeader
        title="Website"
        description={DESCRIPTION}
        actions={
          canManage.data === true ? (
            <Button
              leftSection={<IconUpload size={16} />}
              onClick={() => {
                setAdding(true);
              }}
            >
              Add image
            </Button>
          ) : undefined
        }
      />
      <WebsiteTabs active="images" />
      {assets.isPending ? (
        <LoadingState rows={3} />
      ) : assets.isError ? (
        <Alert color="red" title="Could not load the images">
          <Button
            variant="subtle"
            onClick={() => {
              void assets.refetch();
            }}
          >
            Try again
          </Button>
        </Alert>
      ) : assets.data.length === 0 ? (
        <EmptyState
          title="No images yet"
          description="Add a photo of your building, your people, or whatever a first-time visitor should see."
        />
      ) : (
        <SimpleGrid cols={{ base: 1, xs: 2, md: 3, lg: 4 }} spacing="md">
          {assets.data.map((asset) => (
            <AssetCard key={asset.id} asset={asset} />
          ))}
        </SimpleGrid>
      )}
      <UploadModal
        opened={adding}
        onClose={() => {
          setAdding(false);
        }}
      />
    </>
  );
}
